const hasValue = (v) => v !== null && v !== undefined

const isScalarLike = (v) => {
    if (!hasValue(v)) return false
    if (v instanceof Date) return !isNaN(v.getTime())
    if (v instanceof Uint8Array || v instanceof ArrayBuffer) return true
    return typeof v !== 'object'
}

const normalizeBase64 = (value) => String(value).trim().replace(/^data:[^;]+;base64,/, '').replace(/\s+/g, '')

const isBase64String = (value) => {
    if (typeof value !== 'string') return false
    const normalized = normalizeBase64(value)
    return normalized.length > 0 && normalized.length % 4 === 0 && /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(normalized)
}

const splitUnescaped = (value, separator) => {
    const parts = []
    let current = ''
    let escaped = false

    for (const char of String(value)) {
        if (escaped) {
            current += char
            escaped = false
            continue
        }

        if (char === '\\') {
            current += char
            escaped = true
            continue
        }

        if (char === separator) {
            parts.push(current.trim())
            current = ''
            continue
        }

        current += char
    }

    if (current.trim()) {
        parts.push(current.trim())
    }

    return parts
}

const isValidDN = (value) => {
    const dn = String(value).trim()
    if (!dn) return false

    const rdns = splitUnescaped(dn, ',')
    if (rdns.length === 0) return false

    return rdns.every((rdn) => {
        const attributes = splitUnescaped(rdn, '+')
        return attributes.every((attr) => /^(?:[a-zA-Z][a-zA-Z0-9-]*|\d+(?:\.\d+)*)\s*=\s*.+$/.test(attr))
    })
}

const parseLDAPDate = (value, type) => {
    if (value instanceof Date) return value

    const input = String(value).trim()
    const nativeDate = new Date(input)
    if (!isNaN(nativeDate.getTime())) return nativeDate

    if (type === 'utc time') {
        const match = /^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})?(Z|[+-]\d{4})$/.exec(input)
        if (!match) return new Date(NaN)

        const [, yy, month, day, hour, minute, second = '00', timezone] = match
        const fullYear = Number(yy) >= 50 ? `19${yy}` : `20${yy}`
        const offset = timezone === 'Z' ? 'Z' : `${timezone.slice(0, 3)}:${timezone.slice(3)}`
        return new Date(`${fullYear}-${month}-${day}T${hour}:${minute}:${second}${offset}`)
    }

    const match = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(?:\.(\d+))?(Z|[+-]\d{4})?$/.exec(input)
    if (!match) return new Date(NaN)

    const [, year, month, day, hour, minute, second, fraction = '', timezone = 'Z'] = match
    const milliseconds = fraction.slice(0, 3).padEnd(3, '0') || '000'
    const offset = timezone === 'Z' ? 'Z' : `${timezone.slice(0, 3)}:${timezone.slice(3)}`
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds}${offset}`)
}

const toUint8Array = (value) => {
    if (value instanceof Uint8Array) return value
    if (value instanceof ArrayBuffer) return new Uint8Array(value)
    if (typeof Buffer !== 'undefined' && value instanceof Buffer) return new Uint8Array(value)
    if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer, value.byteOffset, value.byteLength)
    if (typeof value === 'string') return new TextEncoder().encode(value)
    return null
}

const validators = {
    // String-like LDAP syntaxes
    'directory string': (v) => isScalarLike(v),
    'ia5 string': (v) => isScalarLike(v) && /^[\x00-\x7F]*$/.test(String(v)),
    'printable string': (v) => isScalarLike(v) && /^[A-Za-z0-9 '()+,\-./:=?]*$/.test(String(v)),
    'numeric string': (v) => isScalarLike(v) && /^[0-9 ]+$/.test(String(v).trim()),
    'telephone number': (v) => isScalarLike(v) && /^\+?[0-9()\-.\s]{3,}$/.test(String(v).trim()),
    dn: (v) => typeof v === 'string' && isValidDN(v),
    oid: (v) => /^\d+(?:\.\d+)+$/.test(String(v).trim()),
    uri: (v) => {
        try {
            new URL(String(v))
            return true
        } catch {
            return false
        }
    },

    // Primitive LDAP syntaxes
    boolean: (v) => {
        if (typeof v === 'boolean') return true
        if (typeof v === 'number') return v === 0 || v === 1
        if (typeof v === 'string') return /^(true|false|yes|no|t|f|1|0)$/i.test(v.trim())
        return false
    },
    integer: (v) => {
        if (v === null || v === undefined) return false
        if (typeof v === 'number') return Number.isInteger(v)

        const normalizedValue = String(v).trim()
        if (normalizedValue === '') return false

        return /^-?\d+$/.test(normalizedValue)
    },

    // Time syntaxes
    'generalized time': (v) => {
        if (v instanceof Date) return !isNaN(v.getTime())
        const s = String(v).trim()
        return /^\d{14}(?:\.\d+)?(?:Z|[+-]\d{4})?$/.test(s) || !isNaN(Date.parse(s))
    },
    'utc time': (v) => {
        if (v instanceof Date) return !isNaN(v.getTime())
        const s = String(v).trim()
        return /^\d{10}(?:\d{2})?(?:Z|[+-]\d{4})$/.test(s) || !isNaN(Date.parse(s))
    },

    // Binary syntaxes
    'octet string': (v) => typeof v === 'string' || v instanceof Uint8Array || v instanceof ArrayBuffer || ArrayBuffer.isView(v),
    jpeg: (v) => v instanceof Uint8Array || v instanceof ArrayBuffer || ArrayBuffer.isView(v) || isBase64String(v),
    sid: (v) => /^(S-\d(-\d+)+)$/i.test(String(v).trim()) || v instanceof Uint8Array || v instanceof ArrayBuffer || ArrayBuffer.isView(v),

    // Extra helper types
    uuid: (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(v).trim()),
    json: (v) => {
        if (typeof v === 'object') return v !== null
        try {
            JSON.parse(v)
            return true
        } catch {
            return false
        }
    },
    array: (v) => Array.isArray(v),
}

const parsers = {
    'directory string': (v) => String(v),
    'ia5 string': (v) => String(v),
    'printable string': (v) => String(v),
    'numeric string': (v) => String(v).trim(),
    'telephone number': (v) => String(v).trim(),
    dn: (v) => String(v).trim(),
    oid: (v) => String(v).trim(),
    uri: (v) => String(v).trim(),

    boolean: (v) => {
        if (typeof v === 'boolean') return v
        if (v === 1 || /^(true|yes|t|1)$/i.test(String(v).trim())) return true
        return false
    },
    integer: (v) => Number(String(v).trim()),

    'generalized time': (v) => parseLDAPDate(v, 'generalized time'),
    'utc time': (v) => parseLDAPDate(v, 'utc time'),

    'octet string': (v) => toUint8Array(v),
    jpeg: (v) => toUint8Array(typeof v === 'string' ? normalizeBase64(v) : v),
    sid: (v) => v instanceof Uint8Array || v instanceof ArrayBuffer || ArrayBuffer.isView(v) ? toUint8Array(v) : String(v).trim(),

    uuid: (v) => String(v).trim().toLowerCase(),
    json: (v) => typeof v === 'object' ? v : JSON.parse(v),
    array: (v) => Array.from(v),
}

export function createLDAPValidator(ldapType) {
    const normalizedInput = String(ldapType).toLowerCase().trim().replace(/\s+/g, ' ')
    const baseType = normalizedInput.replace(/\(.+\)/, '').trim()

    const typeAliases = {
        string: 'directory string',
        directorystring: 'directory string',
        'case ignore string': 'directory string',
        cis: 'directory string',
        distinguishedname: 'dn',
        'distinguished name': 'dn',
        name: 'dn',
        int: 'integer',
        bigint: 'integer',
        smallint: 'integer',
        tinyint: 'integer',
        number: 'integer',
        generalizedtime: 'generalized time',
        datetime: 'generalized time',
        'date time': 'generalized time',
        utctime: 'utc time',
        numericstring: 'numeric string',
        ia5string: 'ia5 string',
        printablestring: 'printable string',
        telephonenumber: 'telephone number',
        octetstring: 'octet string',
        binary: 'octet string',
        photo: 'jpeg',
        'jpeg photo': 'jpeg',
        guid: 'uuid',
        objectguid: 'uuid',
        url: 'uri',
        'object identifier': 'oid',
        objectsid: 'sid',
        'security identifier': 'sid',
        list: 'array',
    }

    const normalizedType = typeAliases[baseType] || baseType
    const validatorFn = validators[normalizedType]
    const parserFn = parsers[normalizedType]

    if (!validatorFn || !parserFn) {
        throw new Error(`Unsupported LDAP type: "${ldapType}"`)
    }

    return {
        type: normalizedType,
        validate(value) {
            if (value === null || value === undefined) {
                return { valid: false, type: normalizedType, value, parsed: null, error: 'Value is null or undefined' }
            }

            const isValid = validatorFn(value)
            return {
                valid: isValid,
                type: normalizedType,
                value,
                parsed: isValid ? parserFn(value) : null,
                ...(isValid ? {} : { error: `Value "${value}" is not a valid ${normalizedType}` }),
            }
        },
    }
}